import * as React from 'react'
import loadable from '@loadable/component'
import { useRouter } from 'next/compat/router'
import { vsprintf } from 'sprintf-js'

import {
  i18n,
  useFormattableI18nProperty,
  useSiteConfig,
} from '@thg-commerce/enterprise-core'
import { ReviewElementWithFeatures } from '@thg-commerce/enterprise-network/src/transformers/product/reviews'
import { useTheme } from '@thg-commerce/enterprise-pages/src/Product/theme'
import {
  FeatureRatingDisplay,
  ProductReview,
  RatingSize,
  VisuallyHidden,
} from '@thg-commerce/gravity-elements'
import { Star } from '@thg-commerce/gravity-elements/Product/ProductReview/Star'
import { GridItem } from '@thg-commerce/gravity-system'
import { spacing } from '@thg-commerce/gravity-theme'

const SvgClose = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/Close'),
  { ssr: true, fallback: <div style={{ width: 16, height: 16 }} /> },
)

import {
  AverageFeatureRatingsTitle,
  ButtonsWrapper,
  CreateReviewButton,
  NumberOfReviews,
  ProductRatingContainer,
  RatingElement,
  RatingElementsContainer,
  ReviewAndBreakdownWrapper,
  ReviewIndex,
  ReviewsCount,
  SimpleBar,
  StarBreakdownWrapper,
  StarContainer,
  StarWrapper,
  StyledBar,
  StyledProductTags,
  ViewAllReviewsButton,
} from './styles'

export interface ProductRatingProps {
  starRating: number
  numberOfReviews: number
  count1Score: number
  count2Score: number
  count3Score: number
  count4Score: number
  count5Score: number
  averageFeatureScores?: ReviewElementWithFeatures[]
  createReviewLink: string
  viewAllReviewsLink?: string
  showInlineFeatureScores?: boolean
  filters?: { scores: string[] }
  url: string
  showReviews?: boolean
  enableFocusOutline?: boolean
}

enum CtaType {
  CREATE_REVIEW = 'createReviewCTA',
  VIEW_ALL_REVIEWS = 'viewAllReviewsCTA',
}

const getSelectedStars = (
  filters: { scores?: string[] },
  star: string,
  maxLength: number,
) => {
  if (!filters?.scores) {
    filters.scores = []
  }

  if (filters.scores?.includes(star)) {
    const filteredStars = filters.scores
      .filter((value) => value !== star)
      .join('|')

    if (filteredStars) {
      return `scores:${filteredStars}`
    }
    return ''
  }

  if (filters.scores.length === maxLength - 1) {
    return ''
  }

  filters.scores.push(star)
  return `scores:${filters.scores.join('|')}`
}

const showViewAllReviewsButton = (
  viewAllReviewsLink: string | undefined,
  showExtraReviewsCTAs: boolean,
  numberOfReviews: number,
  productPageMaxReviewsDisplayed: number,
) =>
  viewAllReviewsLink &&
  showExtraReviewsCTAs &&
  numberOfReviews > productPageMaxReviewsDisplayed

export const ProductRating = (props: ProductRatingProps) => {
  const {
    enableReviewFormFeatureRatings,
    productPageMaxReviewsDisplayed,
    removeReviewBars,
  } = useSiteConfig()

  const showExtraReviewsCTAs =
    props.showReviews === undefined || props.showReviews

  const starReviewCountMap = {
    5: props.count5Score,
    4: props.count4Score,
    3: props.count3Score,
    2: props.count2Score,
    1: props.count1Score,
  }

  const i18nText = {
    reviewsStarsLabel: i18n('reviews.stars.label'),
    reviewsStarsAriaLabel: i18n('general.productreview.label'),
    reviewSingleStarsAriaLabel: i18n('general.productreviewsingle.label'),
    reviewsCounterSingleSuffix: i18n('reviews.reviewscountersinglesuffix.text'),
    reviewsCounterSuffix: i18n('reviews.reviewscountersuffix.text'),
    createAReview: i18n('reviews.createreview.text'),
    viewAllReviews: i18n('reviews.viewreviews.text'),
    averageFeatureTitle: i18n('product.review.featurerating.average.title'),
    filterPillTextSingleStar: i18n(
      'product.productrating.score.filter.pill.text.single.star',
    ),
    filterPillTextMultipleStar: useFormattableI18nProperty(
      'product.productrating.score.filter.pill.text.multiple.stars',
    ),
  }

  const router = useRouter()
  const theme = useTheme()
  const { enabled: enabledFilterReviewsByScore, colorFills: filterColorFills } =
    theme.pageTheme?.productRating.filter || {}

  const starNumbers = Object.keys(starReviewCountMap).reverse()
  const starCounts = starNumbers.map((num) => {
    return (
      <ReviewIndex
        textStyle={theme.pageTheme?.productRating.starNumber.textStyle}
      >
        {num}
      </ReviewIndex>
    )
  })

  const handleFilterSelect = (numberOfStars: string) => {
    if (!showExtraReviewsCTAs) return

    const selectedFilters = getSelectedStars(
      props.filters || {},
      numberOfStars,
      Object.keys(starReviewCountMap).length,
    )

    const { sku, reviewFilters, pageNumber, ...queryProps } = router.query

    if (selectedFilters) {
      queryProps.reviewFilters = selectedFilters
    }

    router.push({
      pathname: props.url,
      hash: 'reviews',
      query: { ...queryProps },
    })
  }

  const starBreakdownBars = starNumbers.map((numberOfStars) => {
    const reviewsCount = starReviewCountMap[numberOfStars]
    const barWidth =
      reviewsCount !== 0 ? (reviewsCount / props.numberOfReviews) * 100 : 0
    const ariaLabel =
      reviewsCount === 1
        ? i18nText.reviewSingleStarsAriaLabel
        : i18nText.reviewsStarsAriaLabel
    const ratingContainerAriaLabel = vsprintf(ariaLabel, [
      numberOfStars,
      5,
      reviewsCount,
    ])

    const filterReviewsByScore = enabledFilterReviewsByScore && reviewsCount > 0

    return (
      <React.Fragment key={numberOfStars}>
        <VisuallyHidden text={ratingContainerAriaLabel} />
        <RatingElement aria-hidden={true}>
          <StarWrapper>
            <Star
              fillValue={5}
              colorFills={
                theme.pageTheme?.productRating.individualStar.colorFills
              }
            />
          </StarWrapper>
          <SimpleBar
            onClick={() => {
              if (filterReviewsByScore) {
                handleFilterSelect(numberOfStars)
              }
            }}
            as={filterReviewsByScore && showExtraReviewsCTAs ? 'button' : 'div'}
            filterReviewsByScore={filterReviewsByScore}
            colorFills={{
              ...filterColorFills,
              hover: showExtraReviewsCTAs
                ? filterColorFills?.hover
                : 'currentcolor',
            }}
            backgroundColor={
              theme.pageTheme?.productRating.bar.colorFills.background
            }
          >
            <StyledBar
              barWidth={barWidth}
              isSelected={!!props.filters?.scores?.includes(numberOfStars)}
              colorFills={filterColorFills}
              fill={theme.pageTheme?.productRating.bar.colorFills.fill}
            />
          </SimpleBar>
        </RatingElement>
      </React.Fragment>
    )
  })

  const starBreakdownAmounts = starNumbers.map((numberOfStars) => {
    return (
      <React.Fragment key={numberOfStars}>
        <ReviewsCount
          textStyle={theme.pageTheme?.productRating.reviewsCount.textStyle}
          aria-hidden="true"
        >
          {starReviewCountMap[numberOfStars]}
        </ReviewsCount>
      </React.Fragment>
    )
  })
  const reviewsSuffix =
    props.numberOfReviews === 1
      ? i18nText.reviewsCounterSingleSuffix
      : i18nText.reviewsCounterSuffix

  const handleTagButton = (_, index: number) => {
    handleFilterSelect(
      props.filters?.scores.slice(index, index + 1).toString() || '',
    )
  }

  const getFilterTags = () => {
    return (
      props.filters?.scores.map((value) => {
        if (value === '1') {
          return i18nText.filterPillTextSingleStar
        }
        return i18nText.filterPillTextMultipleStar(value)
      }) || []
    )
  }
  const viewAllReviewsButton = showViewAllReviewsButton(
    props.viewAllReviewsLink,
    showExtraReviewsCTAs,
    props.numberOfReviews,
    productPageMaxReviewsDisplayed,
  )

  const getCTAEmphasis = (ctaType: CtaType) => {
    return theme.pageTheme?.productRating.CTAs?.[ctaType]?.emphasis || 'low'
  }

  const RenderReviewStarBreakdown = ({
    removeReviewBars,
    starCounts,
    starBreakdownBars,
    starBreakdownAmounts,
  }: {
    removeReviewBars: boolean
    starCounts: React.ReactNode
    starBreakdownBars: React.ReactNode
    starBreakdownAmounts: React.ReactNode
  }) => {
    if (!removeReviewBars) return null

    return (
      <StarBreakdownWrapper>
        <StarContainer>{starCounts}</StarContainer>
        <RatingElementsContainer>{starBreakdownBars}</RatingElementsContainer>
        <StarContainer>{starBreakdownAmounts}</StarContainer>
      </StarBreakdownWrapper>
    )
  }

  return (
    <ProductRatingContainer>
      <ReviewAndBreakdownWrapper
        colSpan={props.showInlineFeatureScores ? [12, 12, 4, 4] : 12}
        colStart={1}
      >
        <ProductReview
          starRating={props.starRating}
          screenReaderOnlyText={`${props.starRating} ${i18nText.reviewsStarsLabel}`}
          showAverage
          size={RatingSize.Large}
          reviewsCounterSuffixText={reviewsSuffix}
          colorFills={theme.pageTheme?.productRating.averageStars.colorFills}
          enableFocusOutline={props.enableFocusOutline}
        />
        <NumberOfReviews
          textStyle={theme.pageTheme?.productRating.totalReviews.textStyle}
        >
          {props.numberOfReviews} {reviewsSuffix}
        </NumberOfReviews>
        {props.filters?.scores && (
          <StyledProductTags
            tags={getFilterTags()}
            tagButton={{
              onClick: handleTagButton,
              icon: (
                <SvgClose
                  width={16}
                  height={16}
                  fill={theme.pageTheme?.productRating.tag?.colorFills.primary}
                  style={{
                    border: `1px solid ${theme.pageTheme?.productRating.tag?.colorFills.secondary}`,
                    borderRadius: '50%',
                    background: `${theme.pageTheme?.productRating.tag?.colorFills.secondary}`,
                  }}
                />
              ),
            }}
            colors={{
              tagColor: theme.pageTheme?.productRating.tag?.colorFills.primary,
              textColor:
                theme.pageTheme?.productRating.tag?.colorFills.secondary,
            }}
          />
        )}
        <RenderReviewStarBreakdown
          removeReviewBars={!removeReviewBars}
          starCounts={starCounts}
          starBreakdownBars={starBreakdownBars}
          starBreakdownAmounts={starBreakdownAmounts}
        />
      </ReviewAndBreakdownWrapper>
      {enableReviewFormFeatureRatings &&
        props.averageFeatureScores &&
        props.averageFeatureScores.length !== 0 && (
          <GridItem
            colSpan={props.showInlineFeatureScores ? [12, 12, 4, 4] : 12}
            colStart={props.showInlineFeatureScores ? [1, 1, 9, 9] : 1}
            rowSpan={2}
            rowStart={props.showInlineFeatureScores ? [2, 2, 1, 1] : 2}
            style={{
              marginBottom: spacing(3.75),
            }}
          >
            <AverageFeatureRatingsTitle>
              {i18nText.averageFeatureTitle}
            </AverageFeatureRatingsTitle>
            <FeatureRatingDisplay featureRatings={props.averageFeatureScores} />
          </GridItem>
        )}
      <ButtonsWrapper colSpan={12} colStart={1}>
        {viewAllReviewsButton && (
          <ViewAllReviewsButton
            emphasis={getCTAEmphasis(CtaType.VIEW_ALL_REVIEWS)}
            renderedAs="a"
            href={props.viewAllReviewsLink}
            border={
              theme.pageTheme?.productRating.CTAs?.viewAllReviewsCTA?.border
            }
          >
            {i18nText.viewAllReviews}
          </ViewAllReviewsButton>
        )}
        <CreateReviewButton
          emphasis={getCTAEmphasis(CtaType.CREATE_REVIEW)}
          renderedAs="a"
          href={props.createReviewLink}
          border={theme.pageTheme?.productRating.CTAs?.createReviewCTA?.border}
        >
          {i18nText.createAReview}
        </CreateReviewButton>
      </ButtonsWrapper>
    </ProductRatingContainer>
  )
}
